import { Component, OnInit, ViewChild, TemplateRef, inject } from "@angular/core";
import { GenericGroupedListConfig } from "@app/shared/models/GenericList/GenericGroupedListConfig";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { Pages } from "@constants/pages/Pages";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TabService } from "@services/tabs/TabService";
import { OverviewDataService } from "./shared/adv-eff-overview-data.service";
import { WizardComponent } from "./wizard/adv-eff-wizard.component";
import { EffectivenessControlOverviewModel } from "./shared/effectiveness-control-overview.model";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { finalize } from "rxjs/operators";
import { ControlDataService } from "@app/controls/services/control.data.service";
import { ControlPermissionModel } from "@app/controls/models/ControlPermissionsModel";
import { IdNameColorModel } from "@models/generic/IdNameColorModel";
import { of, firstValueFrom } from "rxjs";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { HistoryOverviewComponent } from "@app/shared/history-overview/history-overview.component";
import { AdvEffHistoryDataService } from "./adv-eff-history-data.service";
import { nameof } from "@methods/jeffs-toolkit";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { TestperiodManagerComponent } from "./testperiod-manager/adv-eff-testperiod-manager.component";
import { AuditSamplerComponent } from "./audit-sampler/adv-eff-audit-sampler.component";
import { ControlModule } from "@app/controls/control.module";
import { GenericGroupedListManagerComponent } from "@app/shared/generic-managers/generic-grouped-list-manager/generic-grouped-list-manager.component";

@Component({
    selector: "adv-eff-control-overview",
    templateUrl: "./adv-eff-control-overview.component.html",
    styleUrls: ["./adv-eff-control-overview.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [
        CerrixLoaderComponent,
        MenuSystemDirective,
        TranslatePipe,
        WizardComponent,
        HistoryOverviewComponent,
        TestperiodManagerComponent,
        AuditSamplerComponent,
        GenericGroupedListManagerComponent,
        ControlModule,
    ],
})
export class AdvEffControlOverviewComponent implements OnInit {
    private ds = inject(OverviewDataService);
    private pages = inject(Pages);
    private tabService = inject(TabService);
    private modalService = inject(BsModalService);
    private controlDs = inject(ControlDataService);
    advancedEffectivenessHistoryDataService = inject(AdvEffHistoryDataService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @ViewChild("testWizard") testWizard: WizardComponent;
    @ViewChild(HistoryOverviewComponent) historyOverviewComponent;

    tab: TabModel;
    id: any;
    effectivenessOverviewConfig: GenericGroupedListConfig;
    modalRef: BsModalRef;
    permissions: ControlPermissionModel;
    scores: IdNameColorModel[];
    hideActionBar = false;

    async ngOnInit(): Promise<void> {
        await this._translation.ready();

        this.controlDs.getPermissions(this.id).subscribe(
            (perm) => {
                this.permissions = perm;
                this.controlDs.getAllScores().subscribe((d) => {
                    this.scores = d;
                    this.createOverviewConfig();

                    this.tab.showLoader = false;
                });
            },
            () => this.tab.close(false)
        );
    }

    createOverviewConfig(): void {
        this.effectivenessOverviewConfig = <GenericGroupedListConfig>{
            name: this._translation.t("advEffControlOverview.testPlansListName"),
            groupByProp: nameof<EffectivenessControlOverviewModel>((eff) => eff.groupId),
            groupByDisplayName: nameof<EffectivenessControlOverviewModel>((eff) => eff.groupName),
            overviewSortBy: nameof<EffectivenessControlOverviewModel>((eff) => eff.endDate),
            overviewSortAsc: true,

            idProp: nameof<EffectivenessControlOverviewModel>((eff) => eff.guid),

            allowAdd: this.permissions.CanManageEffectivenesses,
            allowEdit: this.permissions.CanManageEffectivenesses,
            allowDelete: this.permissions.CanManageEffectivenesses,

            fields: [
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.testPlanName),
                    prettyName: this._translation.t("advEffControlOverview.colTestPlanName"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.statusDescription
                    ),
                    prettyName: this._translation.t("advEffControlOverview.colStatus"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.startDate),
                    prettyName: this._translation.t("advEffControlOverview.colStartDate"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.endDate),
                    prettyName: this._translation.t("advEffControlOverview.colEndDate"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.numberOfSamples
                    ),
                    prettyName: this._translation.t("advEffControlOverview.colNumSamples"),
                    fieldType: GenericListFieldType.Number,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.numberOfSourceDocuments
                    ),
                    prettyName: this._translation.t("advEffControlOverview.colNumSourceDocuments"),
                    fieldType: GenericListFieldType.Number,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.score),
                    prettyName: this._translation.t("advEffControlOverview.colScore"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    getDataMethod: () => {
                        return of(this.scores);
                    },
                },
            ],

            cellConfig: {
                testPlanName: {
                    alignLeft: true,
                },
            },

            cellSummaries: {
                testPlanName: {
                    cellStringFormat: "[embedGroupName] ([embedCount] Test plans)",
                    embedGroupName: true,
                    embedCount: true,
                },
                startDate: {
                    cellStringFormat:
                        "{{" +
                        nameof<EffectivenessControlOverviewModel>((eff) => eff.groupStartDate) +
                        "}}",
                },
                endDate: {
                    cellStringFormat:
                        "{{" +
                        nameof<EffectivenessControlOverviewModel>((eff) => eff.groupEndDate) +
                        "}}",
                },
                numberOfSamples: {
                    cellStringFormat: "[embedSum]/[embedAvg]",
                    embedSum: true,
                    embedAvg: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.groupMinimumSampleCount
                    ), // This will be minimum sample count in future.
                },
                numberOfSourceDocuments: {
                    cellStringFormat: "[embedSum]",
                    embedSum: true,
                },
                score: {
                    embedColor: { colorCol: "groupScoreColor", textCol: "groupScoreText" },
                },
            },

            overviewRowActionsName: this._translation.t("advEffControlOverview.detailsActionsName"),
            overviewRowActions: [
                {
                    icon: "fas fa-edit",
                    tooltip: this._translation.t("advEffControlOverview.editTestPlanTooltip"),
                    clickEvent: (row: EffectivenessControlOverviewModel) => {
                        this.testWizard.effectivenessGuid = row.guid;
                        this.testWizard.load(true);
                    },
                },
            ],

            addOverride: async () => {
                this.testWizard.effectivenessGuid = null;
                await this.testWizard.load(true);
            },
            editOverride: (row: any) => {
                this.tabService.generateTab(this.pages.ControlsTestingAdvEffDetail, row.guid);
                return false;
            },
            // The reload rebuilds the list, so the manager must not also remove the row from its data.
            deleteOverride: async (row: any) => {
                if (
                    await this._promptService
                        .confirm(
                            this._translation.t("advEffControlOverview.deleteTestPlanTitle"),
                            this._translation.t("advEffControlOverview.deleteTestPlanMessage")
                        )
                        .toPromise()
                ) {
                    const deletingPrompt = this._promptService.loader(
                        this._translation.t("advEffControlOverview.deletingLoader")
                    );

                    await firstValueFrom(this.ds
                        .deleteEffectiveness(row.guid)
                        .pipe(finalize(() => deletingPrompt.close())));

                    await this.effectivenessOverviewConfig.reload();
                }
            },
            dataMethod: () => this.ds.getControlEffectivenesses(this.id, this.scores),
        };
    }

    async reload() {
        await this.effectivenessOverviewConfig.reload();
    }

    openModal(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template, {
            class: "modal-xl",
            animated: true,
            keyboard: false,
            ignoreBackdropClick: true,
        });
    }

    onMenuItemClick(menuItem: TabMenuItem) {
        if (!menuItem || !menuItem.menuItemId) {
            return;
        }

        this.hideActionBar = menuItem.menuItemId !== "effectiveness-menu-item";

        switch (menuItem.menuItemId) {
            case "historymenuitem": {
                this.historyOverviewComponent.loadHistory();
                break;
            }
        }
    }
}
